<script lang="ts">
    import { getContext } from 'svelte'
    import { derived as sderived } from 'svelte/store'
    import type { Readable } from 'svelte/store'

    import Icon from '@iconify/svelte'
    import { toASCII, toUnicode } from 'punycode-esm'

    import { iconExclamationTriangle } from 'hydria/icons'
    import { gettext as _ } from 'hydria/services/gettext'
    import { sgoto, spath } from 'hydria/svelte_helpers'

    import { Breadcrumb } from 'breadcrumb'

    import type { User, UserHandler } from './user'

    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'

    const userh = getContext('User') as UserHandler

    const status = sderived(userh.status, ($status) => $status)
    const loading = sderived(userh.loading, ($loading) => $loading)

    const userId = getContext('userId') as Readable<string>
    const user: Readable<User> = sderived(userh.userWritable, ($v) => $v)

    const WEBNAMES_TLDS = new Set(['quebec', 'cn', 'eco'])
    const OPENSRS_TLD_SKIP = new Set(['cn', 'ca'])

    const crumbs = sderived(status, ($status) => [
        { txt: $_('Users'), url: spath('/wdom-users') },
        {
            txt: $status == 'ready' ? $user.email : $status == 'loading' ? '…' : iconExclamationTriangle,
            html: $status != 'ready' && $status != 'loading',
            url: spath(`/wdom-user/${$userId}`),
        },
        { txt: $_('Importation d’un domaine') },
    ])

    let domainInput = $state('')
    let domainUnicode = $derived(toUnicode(domainInput).toLowerCase())
    let domainIdna = $derived(toASCII(domainUnicode))
    let domainTld = $derived(domainIdna.includes('.') ? domainIdna.split('.').pop() : null)

    let allowedSystems: Set<string> = $state(new Set())
    let system: string | null = $state(null)

    let lastChecked: string | undefined
    function checkSystems(domainTld: string) {
        if (lastChecked == domainTld) {
            return
        }
        lastChecked = domainTld

        if (!domainTld) {
            allowedSystems = new Set()
            system = null
            return
        }

        let lSystems: Set<string> = new Set()
        if (domainTld === 'ca') lSystems.add('cira')
        if (!OPENSRS_TLD_SKIP.has(domainTld)) lSystems.add('opensrs')
        if (WEBNAMES_TLDS.has(domainTld)) lSystems.add('webnames')

        allowedSystems = lSystems
        system = null
        for (const s of ['cira', 'webnames', 'opensrs']) {
            if (allowedSystems.has(s)) {
                system = s
                break
            }
        }
    }

    $effect(() => {
        checkSystems(domainTld)
    })

    function submit() {
        sgoto(`/wdom-user/${$userId}/import/${system}/${domainIdna}`)
    }
</script>

<Breadcrumb {crumbs} />

{#if $status == 'not-found' || $status == 'error'}
    <div class="alert alert-danger">
        <Icon inline icon="fa:exclamation-triangle" />
        {#if $status == 'not-found'}
            {$_('The requested document does not exist.')}
        {:else}
            {$_('An error occured. Please try again in a moment.')}
        {/if}
    </div>
{:else}
    <div class="card custom-email-user-info">
        <div class="card-title text-primary">
            {$_('Importation d’un domaine')}
        </div>

        {#if $loading}
            <div class="card-body">
                <LoadingPattern pattern={['x', 'x', 'x']} />
            </div>
        {:else}
            <div class="card-body">
                <div class="form-group row">
                    <label for="iDomain" class="col-sm-2 col-form-label text-end">{$_('Domain')}</label>
                    <div class="col-sm-8">
                        <input type="text" class="form-control" id="iDomain" bind:value={domainInput} />
                    </div>
                </div>

                {#if domainTld}
                    <div class="form-group row">
                        <label for="iSystem" class="col-sm-2 col-form-label text-end">{$_('System')}</label>
                        <div class="col-sm-10">
                            <div class="btn-group">
                                {#each [['cira', 'CIRA'], ['opensrs', 'OpenSRS'], ['webnames', 'WebNames']] as [sTag, sText]}
                                    {#if allowedSystems.has(sTag)}
                                        <button type="button" class="btn btn-outline-secondary" class:active={system == sTag} onclick={() => (system = sTag)}>{sText}</button>
                                    {/if}
                                {/each}
                            </div>
                        </div>
                    </div>
                {/if}

                {#if system}
                    <div class="offset-sm-3 col-sm-10">
                        <button type="button" class="btn btn-primary" onclick={() => submit()}>Vérifier le domaine et les contacts</button>
                    </div>
                {/if}
            </div>
        {/if}
    </div>
{/if}
